import { useState, useEffect, useMemo, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronDown, ChevronUp, Gauge, Sparkles, Camera, Loader2, Trash2 } from "lucide-react";
import { unitLabel, fromKm, toKm } from "@/lib/units";
import { todayInTz } from "@/lib/tz";
import useTelemetryAutoFill from "@/lib/useTelemetryAutoFill";
import { resizeImage } from "@/lib/imageResize";
import { TripsAPI } from "@/lib/api";
import FuelGaugeInput from "@/components/FuelGaugeInput";
import { toast } from "sonner";
import {
  deriveDistanceKm,
  deriveEnergyUsedKwh,
  deriveEfficiency,
  deriveFuelUsedL,
  deriveLper100km,
} from "@/lib/efficiency";

export default function AddTripDialog({
  open,
  onOpenChange,
  vehicles,
  defaultVehicleId,
  initialTrip = null,   // when present → edit mode
  unit,
  tz,                   // IANA timezone (e.g. "America/Toronto"). Default date is computed in this zone.
  onSubmit,
}) {
  const today = todayInTz(tz);
  const isEdit = !!initialTrip;
  const [vehicleId, setVehicleId] = useState(defaultVehicleId || "");
  const [date, setDate] = useState(today);
  const [distance, setDistance] = useState("");
  const [notes, setNotes] = useState("");
  const [usageType, setUsageType] = useState("personal");
  const [submitting, setSubmitting] = useState(false);

  // Optional EV/Hybrid telemetry
  const [showTelemetry, setShowTelemetry] = useState(false);
  const [startOdo, setStartOdo] = useState("");
  const [endOdo, setEndOdo] = useState("");
  const [startBatt, setStartBatt] = useState("");
  const [endBatt, setEndBatt] = useState("");

  const selectedVehicle = useMemo(
    () => vehicles.find((v) => v.id === vehicleId),
    [vehicles, vehicleId]
  );
  const isElectric =
    selectedVehicle?.energy_type === "ev" || selectedVehicle?.energy_type === "hybrid";
  const isFuelCapable =
    selectedVehicle?.energy_type === "gas" || selectedVehicle?.energy_type === "hybrid";
  const capacity = selectedVehicle?.usable_battery_kwh;
  const tankCapacity = selectedVehicle?.fuel_tank_capacity_l;

  // Fuel telemetry (Gas/Hybrid)
  const [fuelUsed, setFuelUsed] = useState("");
  // Fuel gauge readings — 5 discrete notches (E · ¼ · ½ · ¾ · F). Start is
  // auto-filled from the previous trip's end reading (or reset to F after a
  // fuel-up) so we have visual continuity between trips, mirroring the
  // battery % pattern for EVs. Values: "", "0", "25", "50", "75", "100".
  const [startFuelPct, setStartFuelPct] = useState("");
  const [endFuelPct, setEndFuelPct] = useState("");

  // Dashboard-photo OCR (ephemeral — image is NOT persisted; only used to
  // pre-populate End Odometer + End Battery %). The thumbnail preview lives
  // in a local object URL that we revoke when the dialog closes or the user
  // retakes the photo.
  const dashFileRef = useRef(null);
  const dashCameraRef = useRef(null);
  const [dashPreviewUrl, setDashPreviewUrl] = useState(null);
  const [dashScanning, setDashScanning] = useState(false);

  // Auto-fill: Start Odometer + (EV/Hybrid only) Start Battery % from the
  // vehicle's last-known telemetry. Logic lives in the shared hook so
  // AddEnergyDialog reuses the same stale-closure-safe contract.
  const autoFill = useTelemetryAutoFill({
    open,
    isEdit,
    vehicleId,
    scope: "trip",
    fields: {
      odo: {
        sourceField: "last_odometer_km",
        setValue: setStartOdo,
      },
      batt: {
        sourceField: "current_battery_pct",
        setValue: setStartBatt,
        enabled: isElectric,
      },
      fuel: {
        sourceField: "current_fuel_level_pct",
        setValue: (v) => {
          // Backend returns 0–100; snap to nearest 5% step for slider parity.
          if (v === "" || v == null) {
            setStartFuelPct("");
            return;
          }
          const n = parseFloat(v);
          if (!isFinite(n)) {
            setStartFuelPct("");
            return;
          }
          setStartFuelPct(String(Math.round(n / 5) * 5));
        },
        enabled: isFuelCapable,
      },
    },
  });

  useEffect(() => {
    if (!open) return;
    // Reset the bidirectional derivation tracker — neither effect should fire
    // until the user explicitly touches a Distance or Odometer field.
    lastInputSourceRef.current = null;
    if (initialTrip) {
      // EDIT mode — pre-fill from existing trip. Convert distance back to current display unit.
      setVehicleId(initialTrip.vehicle_id);
      setDate(initialTrip.date);
      const km = initialTrip.distance_km || 0;
      const display = unit === "mi" ? km / 1.60934 : km;
      setDistance(display ? display.toFixed(1) : "");
      setNotes(initialTrip.notes || "");
      setUsageType(initialTrip.usage_type || "personal");
      setStartOdo(initialTrip.start_odometer_km != null ? String(initialTrip.start_odometer_km) : "");
      setEndOdo(initialTrip.end_odometer_km != null ? String(initialTrip.end_odometer_km) : "");
      setStartBatt(initialTrip.start_battery_pct != null ? String(initialTrip.start_battery_pct) : "");
      setEndBatt(initialTrip.end_battery_pct != null ? String(initialTrip.end_battery_pct) : "");
      setFuelUsed(initialTrip.fuel_used_l != null ? String(initialTrip.fuel_used_l) : "");
      setStartFuelPct(
        initialTrip.start_fuel_level_pct != null
          ? String(Math.round(initialTrip.start_fuel_level_pct))
          : ""
      );
      setEndFuelPct(
        initialTrip.end_fuel_level_pct != null ? String(Math.round(initialTrip.end_fuel_level_pct)) : ""
      );
      // Lock the auto-fill hook against ever overwriting saved telemetry.
      autoFill.lockForEdit();
      // Open telemetry section automatically if any telemetry exists on the trip
      const hasTelemetry =
        initialTrip.start_odometer_km != null ||
        initialTrip.end_odometer_km != null ||
        initialTrip.start_battery_pct != null ||
        initialTrip.end_battery_pct != null ||
        initialTrip.start_fuel_level_pct != null ||
        initialTrip.end_fuel_level_pct != null ||
        initialTrip.fuel_used_l != null;
      setShowTelemetry(hasTelemetry);
    } else {
      setVehicleId(defaultVehicleId || vehicles[0]?.id || "");
      setDate(today);
      setDistance("");
      setNotes("");
      setUsageType("personal");
      setShowTelemetry(false);
      setStartOdo("");
      setEndOdo("");
      setStartBatt("");
      setEndBatt("");
      setFuelUsed("");
      setStartFuelPct("");
      setEndFuelPct("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialTrip, defaultVehicleId, vehicles, unit]);

  // Revoke + clear any preview blob URL whenever the dialog closes so we
  // never leak object-URLs across sessions and never persist the photo.
  useEffect(() => {
    if (open) return;
    if (dashPreviewUrl) {
      try { URL.revokeObjectURL(dashPreviewUrl); } catch (_) { /* noop */ }
      setDashPreviewUrl(null);
    }
    setDashScanning(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const clearDashPhoto = () => {
    if (dashPreviewUrl) {
      try { URL.revokeObjectURL(dashPreviewUrl); } catch (_) { /* noop */ }
    }
    setDashPreviewUrl(null);
    if (dashFileRef.current) dashFileRef.current.value = "";
    if (dashCameraRef.current) dashCameraRef.current.value = "";
  };

  const handleDashFile = async (file) => {
    if (!file) return;
    // Replace any previous preview before starting a new scan.
    if (dashPreviewUrl) {
      try { URL.revokeObjectURL(dashPreviewUrl); } catch (_) { /* noop */ }
    }
    setDashScanning(true);
    try {
      const { blob } = await resizeImage(file);
      const localUrl = URL.createObjectURL(blob);
      setDashPreviewUrl(localUrl);
      // Encode to base64 for the JSON OCR endpoint.
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ""));
        reader.onerror = () => reject(new Error("Could not read the image"));
        reader.readAsDataURL(blob);
      });
      const result = await TripsAPI.scanDashboard(base64, "image/jpeg");

      let filled = [];
      if (Number.isFinite(result?.end_odometer_km) && result.end_odometer_km > 0) {
        setEndOdo(String(Math.round(result.end_odometer_km)));
        lastInputSourceRef.current = "odo";
        filled.push("End Odometer");
      }
      if (
        isElectric &&
        Number.isFinite(result?.end_battery_pct) &&
        result.end_battery_pct >= 0 &&
        result.end_battery_pct <= 100
      ) {
        setEndBatt(String(Math.round(result.end_battery_pct)));
        filled.push("End Battery");
      }

      if (filled.length === 0) {
        toast.warning("Couldn't read the dashboard — please enter values manually.");
      } else {
        let msg = `Auto-filled: ${filled.join(" · ")}`;
        if (
          isFuelCapable &&
          Number.isFinite(result?.fuel_level_pct) &&
          result.fuel_level_pct >= 0 &&
          result.fuel_level_pct <= 100
        ) {
          // Snap to nearest 5% step to match the slider granularity while
          // preserving semi-accurate readings from the OCR.
          const snapped = String(Math.round(result.fuel_level_pct / 5) * 5);
          setEndFuelPct(snapped);
          filled.push("End Fuel Level");
          msg = `Auto-filled: ${filled.join(" · ")}`;
        }
        toast.success(msg);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Scan failed";
      toast.error(detail);
    } finally {
      setDashScanning(false);
      if (dashFileRef.current) dashFileRef.current.value = "";
      if (dashCameraRef.current) dashCameraRef.current.value = "";
    }
  };

  // Bidirectional derivation between Distance and Odometer fields:
  //   • User types Start/End Odo  → derive Distance from the delta
  //   • User types Distance + has Start Odo → derive End Odo (start + distance)
  // We track which side the user touched most recently via a ref so the two
  // derivation effects don't fight each other or overwrite the user's input.
  const lastInputSourceRef = useRef(null); // 'distance' | 'odo' | null

  // Derive Distance from odometer delta — only when the user last touched an
  // Odometer field. Skipped during edit-mode init / autofill (both leave the
  // ref at null) and skipped right after the inverse effect sets End Odo.
  useEffect(() => {
    if (lastInputSourceRef.current !== "odo") return;
    const derivedKm = deriveDistanceKm(startOdo, endOdo);
    if (derivedKm != null) {
      const display = fromKm(derivedKm, unit);
      setDistance(display.toFixed(1));
    }
  }, [startOdo, endOdo, unit]);

  // Derive End Odometer when the user typed a Distance and a Start Odo exists.
  // This is the inverse direction the user reported as missing.
  useEffect(() => {
    if (lastInputSourceRef.current !== "distance") return;
    const startKm = parseFloat(startOdo);
    const distVal = parseFloat(distance);
    if (!Number.isFinite(startKm) || !Number.isFinite(distVal) || distVal <= 0) return;
    const distKm = toKm(distVal, unit);
    const computedEnd = startKm + distKm;
    setEndOdo(computedEnd.toFixed(1));
  }, [distance, startOdo, unit]);

  // Reveal the telemetry section automatically when auto-fill surfaces values.
  useEffect(() => {
    if (autoFill.flags.odo || autoFill.flags.batt || autoFill.flags.fuel) {
      setShowTelemetry(true);
    }
  }, [autoFill.flags.odo, autoFill.flags.batt, autoFill.flags.fuel]);

  const energyUsedKwh = useMemo(
    () => (isElectric ? deriveEnergyUsedKwh(startBatt, endBatt, capacity) : null),
    [isElectric, startBatt, endBatt, capacity]
  );

  const efficiency = useMemo(() => {
    const distKm = deriveDistanceKm(startOdo, endOdo);
    if (!isElectric || distKm == null || energyUsedKwh == null) return null;
    return deriveEfficiency(distKm, energyUsedKwh);
  }, [isElectric, startOdo, endOdo, energyUsedKwh]);

  // Fuel used (liters): manual entry wins; else derive from gauge delta ×
  // tank capacity, mirroring the EV kWh-from-battery-delta derivation.
  const derivedFuelUsedL = useMemo(
    () =>
      isFuelCapable ? deriveFuelUsedL(startFuelPct, endFuelPct, tankCapacity) : null,
    [isFuelCapable, startFuelPct, endFuelPct, tankCapacity]
  );

  const effectiveFuelUsedL = useMemo(() => {
    const manual = parseFloat(fuelUsed);
    if (isFinite(manual) && manual > 0) return manual;
    return derivedFuelUsedL;
  }, [fuelUsed, derivedFuelUsedL]);

  const fuelEfficiency = useMemo(() => {
    if (!isFuelCapable) return null;
    // distance in km — convert from current unit field if user hasn't supplied odometers
    const distFromOdo = deriveDistanceKm(startOdo, endOdo);
    let distKm = distFromOdo;
    if (distKm == null && distance) {
      const num = parseFloat(distance);
      if (isFinite(num)) distKm = unit === "mi" ? num * 1.60934 : num;
    }
    return deriveLper100km(distKm, effectiveFuelUsedL);
  }, [isFuelCapable, startOdo, endOdo, distance, unit, effectiveFuelUsedL]);

  const submit = async () => {
    if (!vehicleId || !distance || !date) return;
    setSubmitting(true);
    try {
      const payload = {
        vehicle_id: vehicleId,
        date,
        distance: parseFloat(distance),
        unit,
        notes,
        usage_type: usageType,
        // Stamp the user's active timezone on every entry for audit + replay.
        // Persists per-row independent of future Settings changes.
        entry_timezone: tz || null,
      };
      // Attach telemetry only if at least one telemetry field provided
      if (startOdo || endOdo) {
        payload.start_odometer_km = startOdo ? parseFloat(startOdo) : null;
        payload.end_odometer_km = endOdo ? parseFloat(endOdo) : null;
      }
      if (startBatt || endBatt) {
        payload.start_battery_pct = startBatt ? parseFloat(startBatt) : null;
        payload.end_battery_pct = endBatt ? parseFloat(endBatt) : null;
      }
      if (energyUsedKwh != null) payload.energy_used_kwh = energyUsedKwh;
      if (efficiency) {
        payload.efficiency_km_per_kwh = efficiency.kmPerKwh;
        payload.efficiency_wh_per_km = efficiency.whPerKm;
      }
      // Prefer manual fuel_used; else send the auto-derived value so the
      // saved trip has a concrete liters figure users can see later.
      if (fuelUsed) {
        payload.fuel_used_l = parseFloat(fuelUsed) || null;
      } else if (derivedFuelUsedL != null) {
        payload.fuel_used_l = derivedFuelUsedL;
      }
      if (fuelEfficiency != null) payload.efficiency_l_per_100km = fuelEfficiency;
      if (startFuelPct !== "") payload.start_fuel_level_pct = parseFloat(startFuelPct);
      if (endFuelPct !== "") payload.end_fuel_level_pct = parseFloat(endFuelPct);
      await onSubmit(payload);
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[var(--bg-canvas)] border-[var(--border-subtle)] text-[var(--text-primary)] sm:max-w-[480px] max-h-[90vh] overflow-y-auto"
        data-testid="add-trip-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-light tracking-tight">
            {isEdit ? "Edit Trip" : "Log Trip"}
          </DialogTitle>
          <DialogDescription className="text-[var(--text-muted)]">
            {isEdit ? "Update the details below — efficiency recalculates automatically." : "Add a new trip entry to your mileage log."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="label-tiny">Vehicle</Label>
            <Select value={vehicleId} onValueChange={setVehicleId} disabled={isEdit}>
              <SelectTrigger
                className="bg-[var(--bg-input)] border-[var(--border-subtle)] text-[var(--text-primary)] disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="trip-vehicle-select"
              >
                <SelectValue placeholder="Select vehicle" />
              </SelectTrigger>
              <SelectContent className="bg-[var(--bg-input)] border-[var(--border-subtle)] text-[var(--text-primary)]">
                {vehicles.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="label-tiny">Date</Label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="bg-[var(--bg-input)] border-[var(--border-subtle)] text-[var(--text-primary)] font-mono-data"
                data-testid="trip-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="label-tiny">Distance ({unitLabel(unit)})</Label>
              <Input
                type="number"
                step="0.1"
                min="0"
                value={distance}
                onChange={(e) => {
                  lastInputSourceRef.current = "distance";
                  setDistance(e.target.value);
                }}
                placeholder="0.0"
                className="bg-[var(--bg-input)] border-[var(--border-subtle)] text-[var(--text-primary)] font-mono-data"
                data-testid="trip-distance-input"
              />
            </div>
          </div>

          <div>
            <Label className="label-tiny mb-2 block">Usage</Label>
            <div
              className="inline-flex items-center bg-[var(--bg-input)] border border-[var(--border-subtle)] p-0.5"
              style={{ borderRadius: 2 }}
              data-testid="trip-usage-toggle"
            >
              <button
                type="button"
                onClick={() => setUsageType("personal")}
                data-testid="trip-usage-personal"
                className={`tap px-3 py-1 text-xs tracking-wider transition-colors ${
                  usageType === "personal" ? "bg-[var(--bg-inverse)] text-[var(--text-inverse)]" : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                }`}
                style={{ borderRadius: 1 }}
              >
                Personal
              </button>
              <button
                type="button"
                onClick={() => setUsageType("business")}
                data-testid="trip-usage-business"
                className={`tap px-3 py-1 text-xs tracking-wider transition-colors ${
                  usageType === "business" ? "bg-[var(--bg-inverse)] text-[var(--text-inverse)]" : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                }`}
                style={{ borderRadius: 1 }}
              >
                Business
              </button>
            </div>
          </div>

          {/* Telemetry — available for every vehicle type. EV/Hybrid see
              Battery %; Gas/Hybrid see a fuel-gauge picker for the end-of-trip
              fuel level. Odometer + the Capture Dashboard OCR are universal. */}
          {(isElectric || isFuelCapable) && (
            <div
              className="border border-[var(--border-subtle)] bg-[var(--bg-elevated)]"
              style={{ borderRadius: 4 }}
            >
              <button
                type="button"
                onClick={() => setShowTelemetry((s) => !s)}
                className="w-full flex items-center justify-between px-3 py-2.5 tap"
                data-testid="trip-telemetry-toggle"
              >
                <span className="flex items-center gap-2 label-tiny text-[var(--text-primary)]">
                  <Gauge className="w-3.5 h-3.5 text-[#E82127]" strokeWidth={1.75} />
                  Trip Details · Odometer &amp; Telemetry (optional)
                </span>
                {showTelemetry ? (
                  <ChevronUp className="w-4 h-4 text-[var(--text-muted)]" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-[var(--text-muted)]" />
                )}
              </button>
              {showTelemetry && (
                <div className="px-3 pb-3 pt-1 space-y-3" data-testid="trip-telemetry-section">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="label-tiny h-[24px] flex items-center gap-1.5">
                        <span>Start Odometer (km)</span>
                        {autoFill.flags.odo && (
                          <span
                            className="inline-flex items-center gap-0.5 text-[9px] tracking-wider uppercase text-[#34D17A] font-mono-data"
                            data-testid="trip-start-odo-auto"
                            title="Auto-filled from your last telemetry entry on this vehicle"
                          >
                            <Sparkles className="w-2.5 h-2.5 shrink-0" strokeWidth={2} />
                            Auto
                          </span>
                        )}
                      </Label>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        value={startOdo}
                        onChange={(e) => {
                          autoFill.markUserEdited("odo");
                          lastInputSourceRef.current = "odo";
                          setStartOdo(e.target.value);
                        }}
                        placeholder="—"
                        className="bg-[var(--bg-canvas)] border-[var(--border-subtle)] text-[var(--text-primary)] font-mono-data h-9"
                        data-testid="trip-start-odo"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="label-tiny">End Odometer (km)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        value={endOdo}
                        onChange={(e) => {
                          lastInputSourceRef.current = "odo";
                          setEndOdo(e.target.value);
                        }}
                        placeholder="—"
                        className="bg-[var(--bg-canvas)] border-[var(--border-subtle)] text-[var(--text-primary)] font-mono-data h-9"
                        data-testid="trip-end-odo"
                      />
                    </div>
                    {isElectric && (
                      <>
                    <div className="space-y-1">
                      <Label className="label-tiny h-[24px] flex items-center gap-1.5">
                        <span>Start Battery %</span>
                        {autoFill.flags.batt && (
                          <span
                            className="inline-flex items-center gap-0.5 text-[9px] tracking-wider uppercase text-[#34D17A] font-mono-data"
                            data-testid="trip-start-batt-auto"
                            title="Auto-filled from your last charging session"
                          >
                            <Sparkles className="w-2.5 h-2.5" strokeWidth={2} />
                            Auto
                          </span>
                        )}
                      </Label>
                      <Input
                        type="number"
                        step="1"
                        min="0"
                        max="100"
                        value={startBatt}
                        onChange={(e) => {
                          autoFill.markUserEdited("batt");
                          setStartBatt(e.target.value);
                        }}
                        placeholder="—"
                        className="bg-[var(--bg-canvas)] border-[var(--border-subtle)] text-[var(--text-primary)] font-mono-data h-9"
                        data-testid="trip-start-batt"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="label-tiny">End Battery %</Label>
                      <Input
                        type="number"
                        step="1"
                        min="0"
                        max="100"
                        value={endBatt}
                        onChange={(e) => setEndBatt(e.target.value)}
                        placeholder="—"
                        className="bg-[var(--bg-canvas)] border-[var(--border-subtle)] text-[var(--text-primary)] font-mono-data h-9"
                        data-testid="trip-end-batt"
                      />
                    </div>
                      </>
                    )}
                    {isFuelCapable && (
                      <>
                        {/* Start Fuel Level — auto-filled from previous trip's
                            end reading, or reset to F after a fuel-up. Mirrors
                            the Start Battery % pattern for EVs. Uses a slider
                            + LED-bar display for semi-accurate readings. */}
                        <div className="space-y-1 col-span-2">
                          <Label className="label-tiny flex items-center gap-1.5">
                            <span>Start Fuel Level</span>
                            {autoFill.flags.fuel && (
                              <span
                                className="inline-flex items-center gap-0.5 text-[9px] tracking-wider uppercase text-[#34D17A] font-mono-data"
                                data-testid="trip-start-fuel-auto"
                                title="Auto-filled from your last trip / fuel-up on this vehicle"
                              >
                                <Sparkles className="w-2.5 h-2.5 shrink-0" strokeWidth={2} />
                                Auto
                              </span>
                            )}
                          </Label>
                          <FuelGaugeInput
                            value={startFuelPct}
                            onChange={(v) => {
                              autoFill.markUserEdited("fuel");
                              setStartFuelPct(v);
                            }}
                            ariaLabel="Start of trip fuel gauge reading"
                            testIdPrefix="trip-start-fuel-gauge"
                          />
                        </div>
                        <div className="space-y-1 col-span-2">
                          <Label className="label-tiny">End Fuel Level</Label>
                          <FuelGaugeInput
                            value={endFuelPct}
                            onChange={setEndFuelPct}
                            ariaLabel="End of trip fuel gauge reading"
                            testIdPrefix="trip-end-fuel-gauge"
                          />
                          <div className="text-[10px] text-[var(--text-muted)] tracking-wider">
                            Read the fuel gauge at the end of the trip. Drag the slider or tap a preset to match the needle.
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Capture Dashboard — ephemeral OCR. The image is sent
                      to /api/trips/scan-dashboard for extraction and
                      discarded; only the parsed values land in End
                      Odometer / End Battery for user verification. */}
                  <div
                    className="flex items-start gap-2 pt-1"
                    data-testid="trip-dash-scan-row"
                  >
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        // Prefer camera on mobile; fall back to file picker on desktop.
                        const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
                        const mobile = /Android|iPhone|iPad|iPod|Mobile/i.test(ua);
                        (mobile ? dashCameraRef : dashFileRef).current?.click();
                      }}
                      disabled={dashScanning}
                      className="bg-[var(--bg-canvas)] border-[var(--border-subtle)] hover:bg-[var(--bg-hover)] text-[var(--text-primary)] h-9 text-xs"
                      data-testid="trip-dash-scan-btn"
                    >
                      {dashScanning ? (
                        <>
                          <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" strokeWidth={1.75} />
                          Reading dashboard…
                        </>
                      ) : (
                        <>
                          <Camera className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.75} />
                          {dashPreviewUrl ? "Retake dashboard photo" : "Capture Dashboard"}
                        </>
                      )}
                    </Button>
                    {dashPreviewUrl && (
                      <div className="relative">
                        <img
                          src={dashPreviewUrl}
                          alt="Dashboard preview"
                          className="w-12 h-12 object-cover border border-[var(--border-subtle)]"
                          style={{ borderRadius: 2 }}
                          data-testid="trip-dash-scan-preview"
                        />
                        <button
                          type="button"
                          onClick={clearDashPhoto}
                          aria-label="Remove dashboard photo"
                          className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-black/80 hover:bg-black border border-[#3a1212] text-[#E82127] flex items-center justify-center"
                          style={{ borderRadius: 999 }}
                          data-testid="trip-dash-scan-clear"
                        >
                          <Trash2 className="w-2.5 h-2.5" strokeWidth={1.75} />
                        </button>
                      </div>
                    )}
                    <input
                      ref={dashFileRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/*"
                      className="hidden"
                      onChange={(e) => handleDashFile(e.target.files?.[0])}
                      data-testid="trip-dash-scan-file-input"
                    />
                    <input
                      ref={dashCameraRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      onChange={(e) => handleDashFile(e.target.files?.[0])}
                      data-testid="trip-dash-scan-camera-input"
                    />
                  </div>

                  {/* Derived analytics (Energy Used, Electric Efficiency,
                      Fuel Consumed, Fuel Efficiency) moved into the unified
                      "Trip Analytics" section rendered below the telemetry
                      panel. Keeping this comment as a signpost for future
                      spelunkers. */}
                </div>
              )}
            </div>
          )}

          {/* --------- Trip Analytics (auto) ---------
              Read-only rollup of the numbers derived from the telemetry
              above. Deliberately not editable — the fuel gauge sliders,
              battery %, and odometers are the primary inputs; these tiles
              exist only to surface what we computed. Shells intentionally
              mirror the Battery % input's chrome (same bg / border / height
              / font-mono-data) so the form feels cohesive top-to-bottom.
              Renders only when at least one derived number is available.

              Note on "Electric Efficiency": renamed from plain "Efficiency"
              so it doesn't visually collide with Fuel Efficiency on hybrid
              vehicles where both tiles can show at once. */}
          {(isElectric || isFuelCapable) &&
            (energyUsedKwh != null || efficiency || effectiveFuelUsedL != null || fuelEfficiency != null) && (
            <div
              className="border border-[var(--border-subtle)] bg-[var(--bg-elevated)] p-3 space-y-3"
              style={{ borderRadius: 4 }}
              data-testid="trip-analytics-section"
            >
              <div className="flex items-center gap-2">
                <div className="label-tiny">Trip Analytics</div>
                <span
                  className="inline-flex items-center gap-0.5 text-[9px] tracking-wider uppercase text-[#34D17A] font-mono-data"
                  data-testid="trip-analytics-auto"
                  title="Auto-derived from telemetry — fuel gauge delta × tank capacity, and/or battery delta × usable capacity."
                >
                  <Sparkles className="w-2.5 h-2.5 shrink-0" strokeWidth={2} />
                  Auto
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {isFuelCapable && effectiveFuelUsedL != null && (
                  <div className="space-y-1">
                    <Label className="label-tiny">Fuel Consumed</Label>
                    <div
                      className="bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)] font-mono-data h-9 px-3 flex items-center"
                      style={{ borderRadius: 2 }}
                      data-testid="trip-fuel-used-readonly"
                      aria-readonly="true"
                    >
                      {effectiveFuelUsedL.toFixed(2)}
                      <span className="text-[var(--text-muted)] ml-1">L</span>
                    </div>
                  </div>
                )}
                {isFuelCapable && fuelEfficiency != null && (
                  <div className="space-y-1">
                    <Label className="label-tiny">Fuel Efficiency</Label>
                    <div
                      className="bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)] font-mono-data h-9 px-3 flex items-center"
                      style={{ borderRadius: 2 }}
                      data-testid="trip-fuel-efficiency-readonly"
                      aria-readonly="true"
                    >
                      {fuelEfficiency.toFixed(1)}
                      <span className="text-[var(--text-muted)] ml-1">L/100km</span>
                    </div>
                  </div>
                )}
                {isElectric && energyUsedKwh != null && (
                  <div className="space-y-1">
                    <Label className="label-tiny">Energy Used</Label>
                    <div
                      className="bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)] font-mono-data h-9 px-3 flex items-center"
                      style={{ borderRadius: 2 }}
                      data-testid="trip-energy-used-readonly"
                      aria-readonly="true"
                    >
                      {energyUsedKwh.toFixed(2)}
                      <span className="text-[var(--text-muted)] ml-1">kWh</span>
                    </div>
                  </div>
                )}
                {isElectric && efficiency && (
                  <div className="space-y-1 col-span-2">
                    <Label className="label-tiny">Electric Efficiency</Label>
                    <div
                      className="bg-[var(--bg-canvas)] border border-[var(--border-subtle)] text-[var(--text-primary)] font-mono-data h-9 px-3 flex items-center"
                      style={{ borderRadius: 2 }}
                      data-testid="trip-electric-efficiency-readonly"
                      aria-readonly="true"
                    >
                      {efficiency.kmPerKwh.toFixed(1)}
                      <span className="text-[var(--text-muted)] ml-1">km/kWh</span>
                      <span className="text-[var(--text-faint)] mx-2">·</span>
                      {efficiency.whPerKm.toFixed(0)}
                      <span className="text-[var(--text-muted)] ml-1">Wh/km</span>
                    </div>
                  </div>
                )}
              </div>
              {isFuelCapable && !tankCapacity && (startFuelPct || endFuelPct) && (
                <p className="text-[11px] text-[#FFB340] leading-snug">
                  Tip: set this vehicle&apos;s <span className="text-[var(--text-primary)]">Fuel Tank Capacity</span> in
                  Vehicles → Edit to auto-derive litres consumed from the gauge delta.
                </p>
              )}
              {isElectric && !capacity && (
                <p className="text-[11px] text-[#FFB340] leading-snug">
                  Tip: set this vehicle&apos;s <span className="text-[var(--text-primary)]">Usable Battery Capacity</span> in
                  Vehicles → Edit to unlock automatic energy + efficiency calculations.
                </p>
              )}
            </div>
          )}



          <div className="space-y-2">
            <Label className="label-tiny">Notes (optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Commute, weekend trip, errands..."
              className="bg-[var(--bg-input)] border-[var(--border-subtle)] text-[var(--text-primary)] min-h-[80px]"
              data-testid="trip-notes-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="bg-transparent border-[var(--border-subtle)] text-[var(--text-primary)] hover:bg-[var(--bg-hover)]"
            data-testid="trip-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || !vehicleId || !distance || !date}
            className="bg-[var(--bg-inverse)] text-[var(--text-inverse)] hover:bg-[var(--bg-inverse-hover)]"
            data-testid="trip-submit-btn"
          >
            {submitting ? "Saving..." : (isEdit ? "Save Changes" : "Save Trip")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
